import { useId, useState } from "react";
import type { AttributionFeeCorrection } from "@ondo/shared";
import { ATTRIBUTION_FEE_CORRECTIONS } from "@ondo/shared";

import {
  CORRECTION_LABELS,
  SERVICE_ACTIONS,
  SERVICE_COPY,
  SERVICE_FIELDS,
} from "./attributionService.constants.ts";
import type { CaseCard, LogLine } from "./attributionService.model.ts";
import {
  assignmentRefusal,
  correctionRefusal,
  evidenceRefusal,
  extensionRefusal,
} from "./attributionService.model.ts";

/**
 * The studio confirmation service, on the tab beside the questions it wraps.
 *
 * SIBLING TO `AffiliationClaims` AND `RepresentationRequests`, and it is here
 * for the reason the third of those is: this screen is organised around one
 * question — who is waiting on whom — and a case is that question with a target
 * and a fee attached. A destination of its own would put the studio's queue in
 * two places and make a reviewer check both.
 *
 * WHAT THE VIEW DOES NOT DO. Every rule is in `attributionService.model.ts` and
 * every word is in `attributionService.constants.ts`; the only conditions below
 * are about which controls were opened. `card.mine` and `card.fee.mine` are
 * decided server-side and read here — a `me.memberships` test in this file
 * would be a second copy of a rule the database owns.
 */

type Props = {
  cases: CaseCard[];
  /** The log for the one case a reader has opened, keyed by request. */
  log: Record<string, LogLine[]>;
  busy: boolean;
  onOpenLog: (requestId: string) => void;
  onAskEvidence: (requestId: string, reason: string) => void;
  onExtend: (requestId: string, targetAt: string, reason: string) => void;
  onAssign: (
    requestId: string,
    reviewer: string,
    escalate: boolean,
    reason: string | null,
  ) => void;
  onRecordPaid: (feeId: string, note: string | null) => void;
  onCorrect: (
    feeId: string,
    kind: AttributionFeeCorrection,
    reason: string,
    reference: string | null,
  ) => void;
};

/** Which form, on which case. One at a time, so a reader cannot half-fill two. */
type OpenForm =
  | { kind: "evidence" | "extension" | "reviewer"; requestId: string }
  | { kind: "paid" | "correct"; feeId: string }
  | null;

export function AttributionServiceCases({
  cases,
  log,
  busy,
  onOpenLog,
  onAskEvidence,
  onExtend,
  onAssign,
  onRecordPaid,
  onCorrect,
}: Props) {
  const [open, setOpen] = useState<OpenForm>(null);
  const [reason, setReason] = useState("");
  const [targetAt, setTargetAt] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [escalate, setEscalate] = useState(false);
  const [reference, setReference] = useState("");
  const [correctionKind, setCorrectionKind] =
    useState<AttributionFeeCorrection>("void");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [shown, setShown] = useState<string | null>(null);
  const fieldId = useId();

  const close = () => {
    setOpen(null);
    setReason("");
    setTargetAt("");
    setReviewer("");
    setEscalate(false);
    setReference("");
    setRefusal(null);
  };

  const openOn = (next: NonNullable<OpenForm>) => {
    close();
    setOpen(next);
  };

  const toggleLog = (requestId: string) => {
    if (shown === requestId) {
      setShown(null);
      return;
    }
    setShown(requestId);
    onOpenLog(requestId);
  };

  return (
    <section className="verifications__service">
      <h3 className="verifications__service-heading">{SERVICE_COPY.heading}</h3>
      {/* Both sentences lead, because both are things a reader supplies wrongly
          for themselves: a target is not a guarantee, and a recorded fee is not
          money ONDO holds. */}
      <p className="verifications__service-lead">{SERVICE_COPY.lead}</p>
      <p className="verifications__service-lead">{SERVICE_COPY.feeNote}</p>

      {refusal && (
        <p role="alert" className="verifications__error">
          {refusal}
        </p>
      )}

      <ul className="verifications__list">
        {cases.map((card) => (
          <li
            key={card.requestId}
            className={`verifications__card ${
              card.overdue ? "verifications__card--overdue" : ""
            }`}
          >
            <header className="verifications__card-head">
              <div className="verifications__card-title">
                <h4>{card.title}</h4>
                <p>
                  {card.claimedArtistName ?? ""}
                  {card.ondoCode ? ` · ${card.ondoCode}` : ""}
                </p>
                <p>{card.counterparty}</p>
              </div>
              <div className="verifications__service-stamps">
                <span className="verifications__stamp">{card.stageLabel}</span>
                {card.overdue && (
                  <span className="verifications__stamp verifications__stamp--overdue">
                    {SERVICE_COPY.overdueBadge}
                  </span>
                )}
              </div>
            </header>

            <p className="verifications__service-detail">{card.stageDetail}</p>

            <dl className="verifications__service-facts">
              {card.target && (
                <>
                  <dt>
                    {card.originalTarget
                      ? SERVICE_COPY.revisedLabel
                      : SERVICE_COPY.targetLabel}
                  </dt>
                  <dd>
                    {card.target}
                    {card.originalTarget
                      ? ` · ${SERVICE_COPY.originalLabel} ${card.originalTarget}`
                      : ""}
                  </dd>
                </>
              )}
              <dt>{SERVICE_COPY.reviewerLabel}</dt>
              <dd>
                {card.reviewer ?? SERVICE_COPY.noReviewer}
                {card.reviewer && card.reviewerEscalated
                  ? ` · ${SERVICE_COPY.escalatedLabel}`
                  : ""}
              </dd>
              {card.latestEvidenceReason && (
                <>
                  <dt>{SERVICE_FIELDS.evidenceReason}</dt>
                  <dd>{card.latestEvidenceReason}</dd>
                </>
              )}
            </dl>

            {/* THE FEE, or the sentence that says there is none. An unanswered
                case showing a blank where a figure goes reads as a figure
                somebody failed to load. */}
            {card.fee ? (
              <div className="verifications__service-fee">
                <p>
                  <strong>{card.fee.amount}</strong> · {card.fee.standingLabel}
                </p>
                <p className="verifications__service-detail">{card.fee.standingDetail}</p>
                {card.fee.paid && (
                  <p className="verifications__service-detail">
                    {card.fee.paid}
                    {card.fee.paidByName ? ` · ${card.fee.paidByName}` : ""}
                    {card.fee.paidNote ? ` · ${card.fee.paidNote}` : ""}
                  </p>
                )}
                {card.fee.latestCorrectionReason && (
                  <p className="verifications__service-detail">
                    {card.fee.latestCorrectionReason}
                  </p>
                )}
                {card.fee.mine && (
                  <div className="verifications__service-actions">
                    {!card.fee.paid && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => openOn({ kind: "paid", feeId: card.fee!.feeId })}
                      >
                        {SERVICE_ACTIONS.recordPaid}
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => openOn({ kind: "correct", feeId: card.fee!.feeId })}
                    >
                      {SERVICE_ACTIONS.correct}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="verifications__service-detail">{SERVICE_COPY.noFee}</p>
            )}

            {card.mine && (
              <div className="verifications__service-actions">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => openOn({ kind: "evidence", requestId: card.requestId })}
                >
                  {SERVICE_ACTIONS.evidence}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => openOn({ kind: "extension", requestId: card.requestId })}
                >
                  {SERVICE_ACTIONS.extension}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => openOn({ kind: "reviewer", requestId: card.requestId })}
                >
                  {SERVICE_ACTIONS.reviewer}
                </button>
              </div>
            )}

            <button
              type="button"
              className="verifications__service-log-toggle"
              onClick={() => toggleLog(card.requestId)}
            >
              {SERVICE_COPY.logHeading}
              {card.extensionCount + card.evidenceRequestCount > 0
                ? ` (${String(card.extensionCount + card.evidenceRequestCount + 1)})`
                : ""}
            </button>

            {shown === card.requestId && (
              <ol className="verifications__service-log">
                {(log[card.requestId] ?? []).map((line) => (
                  <li key={line.id}>
                    <strong>{line.label}</strong>
                    {line.target ? ` · ${line.target}` : ""}
                    {line.actedBy ? ` · ${line.actedBy}` : ""}
                    {line.reason ? ` · ${line.reason}` : ""}
                    <span className="verifications__service-detail"> · {line.recorded}</span>
                  </li>
                ))}
              </ol>
            )}

            {open?.kind === "evidence" && open.requestId === card.requestId && (
              <form
                className="verifications__service-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  const stop = evidenceRefusal(reason);
                  setRefusal(stop);
                  if (stop) return;
                  onAskEvidence(card.requestId, reason.trim());
                  close();
                }}
              >
                <p className="verifications__service-detail">{SERVICE_COPY.evidenceNote}</p>
                <label htmlFor={`${fieldId}-evidence`}>{SERVICE_FIELDS.evidenceReason}</label>
                <textarea
                  id={`${fieldId}-evidence`}
                  value={reason}
                  placeholder={SERVICE_FIELDS.evidencePlaceholder}
                  onChange={(event) => setReason(event.target.value)}
                />
                <button type="submit" disabled={busy}>
                  {SERVICE_ACTIONS.evidence}
                </button>
              </form>
            )}

            {open?.kind === "extension" && open.requestId === card.requestId && (
              <form
                className="verifications__service-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  const stop = extensionRefusal({ targetAt, reason });
                  setRefusal(stop);
                  if (stop) return;
                  // A date input gives a plain day; the server wants a zoned
                  // instant, and midnight UTC is what "that day" means to two
                  // parties in different places.
                  onExtend(card.requestId, `${targetAt}T00:00:00.000Z`, reason.trim());
                  close();
                }}
              >
                <p className="verifications__service-detail">{SERVICE_COPY.extensionNote}</p>
                <label htmlFor={`${fieldId}-date`}>{SERVICE_FIELDS.extensionDate}</label>
                <input
                  id={`${fieldId}-date`}
                  type="date"
                  value={targetAt}
                  onChange={(event) => setTargetAt(event.target.value)}
                />
                <label htmlFor={`${fieldId}-why`}>{SERVICE_FIELDS.extensionReason}</label>
                <textarea
                  id={`${fieldId}-why`}
                  value={reason}
                  placeholder={SERVICE_FIELDS.extensionPlaceholder}
                  onChange={(event) => setReason(event.target.value)}
                />
                <button type="submit" disabled={busy}>
                  {SERVICE_ACTIONS.extension}
                </button>
              </form>
            )}

            {open?.kind === "reviewer" && open.requestId === card.requestId && (
              <form
                className="verifications__service-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  const stop = assignmentRefusal({ reviewer, escalate, reason });
                  setRefusal(stop);
                  if (stop) return;
                  onAssign(
                    card.requestId,
                    reviewer.trim(),
                    escalate,
                    reason.trim() || null,
                  );
                  close();
                }}
              >
                {/* SAID ON THE FORM AND NOT ONLY IN THE TICKET. A director
                    assigning a case must not believe they have given somebody
                    the power to answer it. */}
                <p className="verifications__service-detail">{SERVICE_COPY.assignmentNote}</p>
                <label htmlFor={`${fieldId}-reviewer`}>{SERVICE_FIELDS.reviewer}</label>
                <input
                  id={`${fieldId}-reviewer`}
                  value={reviewer}
                  onChange={(event) => setReviewer(event.target.value)}
                />
                <label htmlFor={`${fieldId}-escalate`}>
                  <input
                    id={`${fieldId}-escalate`}
                    type="checkbox"
                    checked={escalate}
                    onChange={(event) => setEscalate(event.target.checked)}
                  />
                  {SERVICE_ACTIONS.escalate}
                </label>
                <label htmlFor={`${fieldId}-reviewer-why`}>
                  {SERVICE_FIELDS.reviewerReason}
                </label>
                <textarea
                  id={`${fieldId}-reviewer-why`}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
                <button type="submit" disabled={busy}>
                  {SERVICE_ACTIONS.reviewer}
                </button>
              </form>
            )}

            {open?.kind === "paid" && card.fee && open.feeId === card.fee.feeId && (
              <form
                className="verifications__service-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  setRefusal(null);
                  onRecordPaid(open.feeId, reason.trim() || null);
                  close();
                }}
              >
                <p className="verifications__service-detail">{SERVICE_COPY.feeNote}</p>
                <label htmlFor={`${fieldId}-paid`}>{SERVICE_FIELDS.paidNote}</label>
                <input
                  id={`${fieldId}-paid`}
                  value={reason}
                  placeholder={SERVICE_FIELDS.paidPlaceholder}
                  onChange={(event) => setReason(event.target.value)}
                />
                <button type="submit" disabled={busy}>
                  {SERVICE_ACTIONS.recordPaid}
                </button>
              </form>
            )}

            {open?.kind === "correct" && card.fee && open.feeId === card.fee.feeId && (
              <form
                className="verifications__service-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  const stop = correctionRefusal(reason);
                  setRefusal(stop);
                  if (stop) return;
                  onCorrect(
                    open.feeId,
                    correctionKind,
                    reason.trim(),
                    reference.trim() || null,
                  );
                  close();
                }}
              >
                <label htmlFor={`${fieldId}-kind`}>{SERVICE_FIELDS.correctionKind}</label>
                <select
                  id={`${fieldId}-kind`}
                  value={correctionKind}
                  onChange={(event) =>
                    setCorrectionKind(event.target.value as AttributionFeeCorrection)
                  }
                >
                  {ATTRIBUTION_FEE_CORRECTIONS.map((kind) => (
                    <option key={kind} value={kind}>
                      {CORRECTION_LABELS[kind]}
                    </option>
                  ))}
                </select>
                <label htmlFor={`${fieldId}-correct-why`}>
                  {SERVICE_FIELDS.correctionReason}
                </label>
                <textarea
                  id={`${fieldId}-correct-why`}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
                <label htmlFor={`${fieldId}-ref`}>{SERVICE_FIELDS.correctionReference}</label>
                <input
                  id={`${fieldId}-ref`}
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                />
                <button type="submit" disabled={busy}>
                  {SERVICE_ACTIONS.correct}
                </button>
              </form>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
